import { useEffect, useState, useCallback, useRef, type KeyboardEvent } from "react";
import { listConversations, createConversation, renameConversation, reorderConversations, listBranches } from "../api/conversations";
import { prefetchTurns } from "../api/turnsCache";
import { isValidBranchName } from "../lib/branchName";
import type { ConversationSummary } from "../api/types";

type Props = {
  projectId: string;
  projectName?: string;
  expanded: boolean;
  // `worktreeDir` rides along so the chat header can show the pinned path
  // without a second fetch. It is NULL on every conversation that has never
  // been renamed, which the header renders as nothing.
  onSelectConversation: (
    conversationId: number,
    branch: string | null,
    additive: boolean,
    worktreeDir?: string | null,
  ) => void;
  onNewConversation?: (conversationId: number, branch: string | null, worktreeDir?: string | null) => void;
  refreshKey?: number;
};

const MAX_ITEMS = 20;

/**
 * How often the list is refetched WHILE something in it is streaming.
 *
 * The streaming dot is server truth, and until now the only thing that
 * refetched it was an event the chat column dispatches for the turn IT is
 * watching. A turn that ended while the user sat in another conversation, in
 * another tab, or on another device therefore left the dot lit with nothing
 * behind it, and the only cure was a manual reload — which is exactly how a
 * member described it ("streaming dot lingers even when its done streaming").
 *
 * Ten seconds is chosen against what the dot is FOR: it is ambient status, not
 * a progress bar, and the open conversation has its own live stream. Polling
 * runs only while at least one conversation actually reads as streaming and
 * only while the tab is visible, so a quiet project and a backgrounded tab both
 * cost nothing.
 */
const STREAMING_POLL_MS = 10_000;

/**
 * The name the daemon would pick for us (src/server.ts:2127). Mirroring it
 * here is what makes the branch visible BEFORE the conversation exists: the
 * field is prefilled with the same thing the user would have got silently, so
 * accepting it costs nothing and changing it costs one edit.
 */
function autoBranchName(): string {
  return `ai/chat-${Date.now().toString(36)}`;
}

/**
 * Id for the shared branch `<datalist>`. Scoped per project because `list=`
 * binds by document-wide id, and kept to a CSS-safe identifier because DOM
 * implementations resolve that binding with a `#id` selector (happy-dom throws
 * outright on anything else, which is why React's `useId` — `:r0:` — is wrong
 * here). The constant prefix guarantees it never starts with a digit.
 */
function branchListIdFor(projectId: string): string {
  return `taiw-conversation-branches-${projectId.replace(/[^a-zA-Z0-9_-]/g, "-")}`;
}

function fmtAgo(tsMs: number | null | undefined): string {
  if (!tsMs) return "";
  const nowMs = Date.now();
  const diffSec = Math.max(0, Math.floor((nowMs - tsMs) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  const diffMo = Math.floor(diffDay / 30);
  if (diffMo < 12) return `${diffMo}mo ago`;
  return `${Math.floor(diffMo / 12)}y ago`;
}

/** The transfer type a dragged conversation row carries. Private, so nothing else accepts it. */
const ROW_DRAG_TYPE = "application/x-taiw-conversation-row";

/** `list` with the row `id` moved to `toIndex` (an index into the list WITHOUT that row). */
export function moveConversation<T extends { id: number }>(list: T[], id: number, toIndex: number): T[] {
  const from = list.findIndex((c) => c.id === id);
  if (from < 0) return list;
  const rest = list.filter((c) => c.id !== id);
  const at = Math.max(0, Math.min(toIndex, rest.length));
  return [...rest.slice(0, at), list[from]!, ...rest.slice(at)];
}

function previewText(c: ConversationSummary): string {
  const p = (c.preview ?? "").trim();
  if (p.length > 0) return p;
  return "(empty conversation)";
}

/** The label shown in the sidebar: a user-set title wins over the message preview. */
function displayName(c: ConversationSummary): string {
  const t = (c.title ?? "").trim();
  return t.length > 0 ? t : previewText(c);
}

export default function ConversationDrawer({
  projectId,
  projectName,
  expanded,
  onSelectConversation,
  onNewConversation,
  refreshKey,
}: Props) {
  const [convs, setConvs] = useState<ConversationSummary[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [err, setErr] = useState<string | null>(null);

  // New-conversation form: which branch the conversation will push to, and
  // what it forks from. Closed by default — the drawer stays a list until the
  // user asks for a new conversation.
  const [newOpen, setNewOpen] = useState<boolean>(false);
  const [newBranch, setNewBranch] = useState<string>("");
  const [newBase, setNewBase] = useState<string>("");
  const [creating, setCreating] = useState<boolean>(false);
  const [branches, setBranches] = useState<string[]>([]);
  const [branchesFailed, setBranchesFailed] = useState<boolean>(false);
  // One datalist feeds both fields.
  const branchListId = branchListIdFor(projectId);

  // Inline rename state. `editingId` mirrors into a ref so the blur handler can
  // tell a real click-away from the synthetic blur fired when the input unmounts
  // (Enter/Escape set editingId=null, which unmounts and re-fires blur).
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft] = useState<string>("");
  const editingIdRef = useRef<number | null>(null);
  const setEditing = useCallback((id: number | null) => {
    editingIdRef.current = id;
    setEditingId(id);
  }, []);

  // Monotonic "data version". Each load() captures it at start and only applies
  // its result if still the latest, so a slow in-flight list fetch cannot stomp
  // newer state. commitRename() bumps it too, so an older read that resolves
  // after a rename cannot revert the just-applied title.
  const loadSeqRef = useRef(0);

  // Drag-to-reorder. `dragIdRef` is the row in hand (null when none); the ref
  // is what the background refreshes read, so a poll cannot reshuffle the list
  // under the pointer. `dropHint` draws the insertion line.
  const dragIdRef = useRef<number | null>(null);
  const [dragId, setDragId] = useState<number | null>(null);
  const [dropHint, setDropHint] = useState<{ id: number; after: boolean } | null>(null);
  const setDragging = useCallback((id: number | null) => {
    // Picking a row up drops any list read already in flight: it would
    // reshuffle the rows under the pointer when it resolves.
    if (id !== null) loadSeqRef.current++;
    dragIdRef.current = id;
    setDragId(id);
    if (id === null) setDropHint(null);
  }, []);
  // Order saves run one at a time, in the order they were made, so a quick
  // second move can never land on the server before the first. While any is
  // outstanding the server still holds an older order, so refreshes wait.
  const saveChainRef = useRef<Promise<void>>(Promise.resolve());
  const pendingSavesRef = useRef(0);
  /** A background refresh must not replace the list mid-rename, mid-drag, or before a save lands. */
  const busy = () => editingIdRef.current !== null || dragIdRef.current !== null || pendingSavesRef.current > 0;
  // `silent` refreshes (live updates from streaming activity) skip the loading
  // skeleton so the list does not flash on every message; only manual/initial
  // loads show it.
  const load = useCallback(async (opts?: { silent?: boolean }) => {
    const seq = ++loadSeqRef.current;
    if (!opts?.silent) setLoading(true);
    setErr(null);
    try {
      const list = await listConversations(projectId);
      if (seq !== loadSeqRef.current) return; // superseded by a newer load/rename
      setConvs(list.slice(0, MAX_ITEMS));
    } catch (e) {
      if (seq !== loadSeqRef.current) return;
      setErr((e as Error).message || "failed to load");
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (!expanded) return;
    void load();
  }, [expanded, refreshKey, load]);

  // Live refresh: when a chat turn streams (useChatStream) it dispatches this
  // event, so the sidebar shows the new/updated conversation name without a
  // page reload. Scoped to this drawer's project.
  useEffect(() => {
    if (!expanded) return;
    const onActivity = (e: Event) => {
      // Do not reorder/replace the list while the user is mid-rename — a
      // background refetch could unmount the row being edited (and lose the
      // typed draft). Refresh resumes once the edit commits/cancels.
      if (busy()) return;
      const detail = (e as CustomEvent<{ project?: string }>).detail;
      if (!detail || detail.project === projectId) void load({ silent: true });
    };
    window.addEventListener("thunderay-ai:conversation-activity", onActivity);
    return () => window.removeEventListener("thunderay-ai:conversation-activity", onActivity);
  }, [expanded, projectId, load]);

  // The event above only ever fires from a column that is WATCHING a turn, so it
  // cannot report a turn that ends while the user is elsewhere. These two close
  // that gap from the other side, by asking the server instead of waiting to be
  // told.
  //
  // Keyed on the derived boolean rather than on `convs`: depending on the array
  // would tear down and rebuild the interval on every refresh, which is a timer
  // that never actually reaches its own deadline.
  const anyStreaming = convs.some((c) => c.streaming === true);
  // A queue drains on the server while nobody is watching; keep the counts
  // fresh. Only a queue that can drain: a paused one (every deploy pauses them
  // all) changes only when its member acts, and polling for it would keep this
  // loop on indefinitely.
  const anyQueued = convs.some((c) => (c.queued ?? 0) > 0 && c.queuePaused !== true);
  useEffect(() => {
    if (!expanded || (!anyStreaming && !anyQueued)) return;
    const id = setInterval(() => {
      // Same rename guard as the event path: a background refetch must not
      // unmount the row being edited and lose the typed draft.
      if (busy()) return;
      if (typeof document !== "undefined" && document.hidden) return;
      void load({ silent: true });
    }, STREAMING_POLL_MS);
    return () => clearInterval(id);
  }, [expanded, anyStreaming, anyQueued, load]);

  // Coming back to a tab that was hidden: whatever was streaming when the user
  // left has very likely finished, and the poll above was deliberately asleep
  // for all of it.
  useEffect(() => {
    if (!expanded) return;
    const onVisible = () => {
      if (document.hidden || busy()) return;
      void load({ silent: true });
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [expanded, load]);

  // Same "latest wins" guard as load(): a slow branch fetch from a form the
  // user already closed (or reopened) must not repopulate the fields.
  // Declared above the `expanded` early return — a hook after it would change
  // hook order the first time a collapsed drawer expands.
  const branchSeqRef = useRef(0);
  const loadBranches = useCallback(async () => {
    const seq = ++branchSeqRef.current;
    try {
      const { branches: list, defaultBranch } = await listBranches(projectId);
      if (seq !== branchSeqRef.current) return;
      // The daemon already puts defaultBranch first and does not repeat it.
      setBranches(list);
      // Do not stomp a base the user typed while the list was in flight.
      setNewBase((cur) => (cur === "" ? defaultBranch : cur));
    } catch {
      if (seq !== branchSeqRef.current) return;
      // A branch list is a convenience, never a gate: the form keeps working
      // with the prefilled name, and an empty base lets the daemon fall back
      // to the project's default branch.
      setBranchesFailed(true);
    }
  }, [projectId]);

  // Every other piece of this component tolerates projectId changing in place
  // (load, the activity listener). The form must too: its branch list, base
  // branch and auto-name all belong to one project, so switching drops it
  // rather than offering another project's branches.
  useEffect(() => {
    branchSeqRef.current++;
    setNewOpen(false);
  }, [projectId]);

  if (!expanded) return null;

  const openNewConversation = () => {
    setErr(null);
    // Rolled HERE, once, when the form opens. Computing it in the render body
    // would re-roll the name on every keystroke, under the user's cursor.
    setNewBranch(autoBranchName());
    setNewBase("");
    setBranches([]);
    setBranchesFailed(false);
    setNewOpen(true);
    void loadBranches();
  };

  const closeNewConversation = () => {
    // Invalidate any branch fetch still in flight from this form.
    branchSeqRef.current++;
    setNewOpen(false);
  };

  const handleNewConversation = async () => {
    if (creating) return;
    const branch = newBranch.trim();
    const baseBranch = newBase.trim();
    // Catch a typo before the round trip. The daemon re-checks with the real
    // `git check-ref-format` and is the authority — this is a courtesy, not the
    // guard, which is why an empty field (meaning "let the daemon decide") is
    // skipped here rather than rejected.
    const badName = [branch, baseBranch].find((n) => n !== "" && !isValidBranchName(n));
    if (badName !== undefined) {
      setErr(`Invalid branch name: ${badName}`);
      return;
    }
    setErr(null);
    setCreating(true);
    try {
      // Omit rather than send empty strings: the daemon treats a missing
      // branch as "auto-name" and a missing base as "project default", which
      // is exactly what a cleared field should mean.
      const row = await createConversation(projectId, {
        ...(branch ? { branch } : {}),
        ...(baseBranch ? { baseBranch } : {}),
      });
      closeNewConversation();
      onNewConversation?.(row.id, row.branch ?? null, row.worktree_dir ?? null);
      void load();
    } catch (e) {
      // Surface the failure in the drawer's existing error UI instead of
      // silently opening no column. The form stays open with what the user
      // typed so a rejected name (400 invalid_branch) can be corrected in
      // place. A branch already open in another conversation is no longer a
      // rejection — it opens a second column in its own worktree.
      setErr((e as Error).message || "Could not start a new conversation");
    } finally {
      setCreating(false);
    }
  };

  const startRename = (c: ConversationSummary) => {
    setEditing(c.id);
    // Prefill with the existing custom title; leave blank (with the message as
    // placeholder) when the conversation was never renamed.
    setDraft((c.title ?? "").trim());
  };

  const cancelRename = () => {
    setEditing(null);
    setDraft("");
  };

  const commitRename = async (id: number) => {
    // Guard the synthetic blur that fires when the input unmounts after a
    // previous commit/cancel set editingId=null.
    if (editingIdRef.current !== id) return;
    const value = draft.trim();
    setEditing(null);
    setDraft("");
    try {
      const stored = await renameConversation(projectId, id, value);
      setConvs((prev) => prev.map((c) => (c.id === id ? { ...c, title: stored } : c)));
      // Invalidate any list read still in flight so it cannot revert this title.
      loadSeqRef.current++;
    } catch (e) {
      setErr((e as Error).message || "Could not rename conversation");
    }
  };

  /**
   * Apply a new order now and save it. The list read counter is bumped so a
   * load already in flight cannot put the old order back; a failed save
   * reloads, which shows the order the server actually kept.
   */
  const applyOrder = async (next: ConversationSummary[]) => {
    const before = convs.map((c) => c.id).join(",");
    if (next.map((c) => c.id).join(",") === before) return;
    loadSeqRef.current++;
    setConvs(next);
    const ids = next.map((c) => c.id);
    pendingSavesRef.current++;
    const save = saveChainRef.current.then(() => reorderConversations(projectId, ids));
    saveChainRef.current = save.catch(() => {});
    try {
      await save;
      pendingSavesRef.current--;
    } catch (e) {
      pendingSavesRef.current--;
      // Reload first: load() clears the error line as it starts.
      await load({ silent: true });
      setErr((e as Error).message || "Could not save the conversation order");
    }
  };

  const dropOn = (targetId: number, after: boolean) => {
    const id = dragIdRef.current;
    setDragging(null);
    if (id === null || id === targetId) return;
    const rest = convs.filter((c) => c.id !== id);
    const at = rest.findIndex((c) => c.id === targetId);
    if (at < 0) return;
    void applyOrder(moveConversation(convs, id, after ? at + 1 : at));
  };

  /** Alt+ArrowUp / Alt+ArrowDown on a row: the keyboard way to reorder. */
  const onRowKeyDown = (e: KeyboardEvent, id: number) => {
    if (!e.altKey || (e.key !== "ArrowUp" && e.key !== "ArrowDown")) return;
    e.preventDefault();
    const from = convs.findIndex((c) => c.id === id);
    const to = e.key === "ArrowUp" ? from - 1 : from + 1;
    if (from < 0 || to < 0 || to >= convs.length) return;
    void applyOrder(moveConversation(convs, id, to));
  };

  return (
    <div className="conversation-drawer">
      {onNewConversation !== undefined && !newOpen && (
        <button
          type="button"
          className="conversation-new"
          onClick={openNewConversation}
        >
          New conversation
        </button>
      )}
      {onNewConversation !== undefined && newOpen && (
        <form
          className="conversation-new-form"
          onSubmit={(e) => { e.preventDefault(); void handleNewConversation(); }}
          onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); closeNewConversation(); } }}
        >
          <label className="conversation-new-field">
            <span>Branch</span>
            <input
              type="text"
              className="conversation-new-input"
              list={branchListId}
              value={newBranch}
              onChange={(e) => setNewBranch(e.target.value)}
              placeholder="ai/my-feature"
              spellCheck={false}
              autoComplete="off"
              autoFocus
            />
          </label>
          <label className="conversation-new-field">
            <span>Base branch</span>
            <input
              type="text"
              className="conversation-new-input"
              list={branchListId}
              value={newBase}
              onChange={(e) => setNewBase(e.target.value)}
              placeholder={branchesFailed ? "project default" : "Loading branches…"}
              spellCheck={false}
              autoComplete="off"
            />
          </label>
          <datalist id={branchListId}>
            {branches.map((b) => <option key={b} value={b} />)}
          </datalist>
          {branchesFailed && (
            <span className="conversation-new-hint">
              Could not load branches — type a name, or leave the base empty for the project default.
            </span>
          )}
          <div className="conversation-new-actions">
            <button type="submit" className="conversation-new-create" disabled={creating}>
              {creating ? "Creating…" : "Create"}
            </button>
            <button
              type="button"
              className="conversation-new-cancel"
              onClick={closeNewConversation}
              disabled={creating}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
      {loading && (
        <div className="conversation-skeleton" aria-label="Loading conversations" role="status">
          <div className="conversation-skeleton-row" />
          <div className="conversation-skeleton-row" style={{ width: "85%" }} />
          <div className="conversation-skeleton-row" style={{ width: "70%" }} />
        </div>
      )}
      {!loading && err && (
        <div className="conversation-error">
          <span>Error: {err}</span>
          <button
            type="button"
            className="conversation-retry"
            onClick={() => { void load(); }}
          >
            Retry
          </button>
        </div>
      )}
      {!loading && !err && convs.length === 0 && (
        <div className="conversation-empty">
          {projectName
            ? `No conversations yet for ${projectName}. Send your first message to start one.`
            : "No conversations yet. Send your first message to start one."}
        </div>
      )}
      {!loading && !err && convs.length > 0 && (
        <ul className="conversation-list">
          {convs.map((c) => {
            const branch = (c as ConversationSummary & { branch?: string | null }).branch ?? null;
            if (editingId === c.id) {
              return (
                <li key={c.id}>
                  <form
                    className="conversation-rename"
                    onSubmit={(e) => { e.preventDefault(); void commitRename(c.id); }}
                  >
                    <input
                      type="text"
                      className="conversation-rename-input"
                      value={draft}
                      placeholder={previewText(c)}
                      autoFocus
                      maxLength={120}
                      aria-label="Conversation name"
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); cancelRename(); } }}
                      onBlur={() => { void commitRename(c.id); }}
                    />
                  </form>
                </li>
              );
            }
            const hint = dropHint?.id === c.id ? (dropHint.after ? " drop-after" : " drop-before") : "";
            return (
              <li
                key={c.id}
                className={`${dragId === c.id ? "dragging" : ""}${hint}`.trim() || undefined}
                onDragOver={(e) => {
                  if (dragIdRef.current === null || !e.dataTransfer.types.includes(ROW_DRAG_TYPE)) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  const box = e.currentTarget.getBoundingClientRect();
                  const after = e.clientY > box.top + box.height / 2;
                  if (dropHint?.id !== c.id || dropHint.after !== after) setDropHint({ id: c.id, after });
                }}
                onDrop={(e) => {
                  if (dragIdRef.current === null || !e.dataTransfer.types.includes(ROW_DRAG_TYPE)) return;
                  e.preventDefault();
                  const box = e.currentTarget.getBoundingClientRect();
                  dropOn(c.id, e.clientY > box.top + box.height / 2);
                }}
              >
                <div
                  className="conversation-row-wrap"
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = "move";
                    e.dataTransfer.setData(ROW_DRAG_TYPE, String(c.id));
                    setDragging(c.id);
                  }}
                  onDragEnd={() => setDragging(null)}
                >
                  <button
                    type="button"
                    className="conversation-row"
                    aria-keyshortcuts="Alt+ArrowUp Alt+ArrowDown"
                    onKeyDown={(e) => onRowKeyDown(e, c.id)}
                    onClick={(e) => onSelectConversation(c.id, branch, e.shiftKey, c.worktree_dir ?? null)}
                    onMouseEnter={() => prefetchTurns(projectId, c.id)}
                    onFocus={() => prefetchTurns(projectId, c.id)}
                    onPointerDown={() => prefetchTurns(projectId, c.id)}
                    title={displayName(c)}
                  >
                    <span className="conversation-preview">{displayName(c)}</span>
                    <span className="conversation-meta">
                      {/* A turn is generating in this conversation on the
                          DAEMON right now — whether or not this browser started
                          it, and whether or not it is even open. Strictly
                          `=== true`: the field is absent on a daemon that
                          predates server-owned turns, and "we do not know" has
                          to render as no dot, not as a dot on every row. */}
                      {c.streaming === true && (
                        <span
                          className="conversation-streaming-dot"
                          data-streaming
                          role="img"
                          aria-label="Generating a response"
                        />
                      )}
                      {typeof c.queued === "number" && c.queued > 0 && (
                        <span className="conversation-queued-count">{c.queued} queued</span>
                      )}
                      {c.turn_count}t · {fmtAgo(c.last_turn_at ?? c.created_at)}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="conversation-rename-btn"
                    aria-label="Rename conversation"
                    title="Rename conversation"
                    onClick={(e) => { e.stopPropagation(); startRename(c); }}
                  >
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M12 20h9" />
                      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z" />
                    </svg>
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
